import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';

export function knowledgeMediaKind(attachment) {
  const type = String(attachment?.content_type || '').toLowerCase();
  const name = String(attachment?.name || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) return 'image';
  if (type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(name)) return 'video';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return 'file';
}

export function isImageAttachment(attachment) {
  return knowledgeMediaKind(attachment) === 'image';
}

export function useKnowledgeEmbedUrl(attachment) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setUrl('');
    setError('');
    if (!attachment?.id) return () => { active = false; };

    api.get(attachment.embed_link_url || `/api/knowledge/files/${attachment.id}/embed-link`)
      .then(response => {
        if (!active) return;
        const nextUrl = response.data?.url;
        if (!nextUrl) throw new Error('Файл недоступен');
        setUrl(nextUrl);
      })
      .catch(err => {
        if (active) setError(err.response?.data?.detail || err.message || 'Файл недоступен');
      });

    return () => { active = false; };
  }, [attachment?.embed_link_url, attachment?.id, version]);

  const retry = useCallback(() => setVersion(current => current + 1), []);
  return { url, error, retry };
}

export default function KnowledgeMedia({ attachment, variant = 'embedded', className = '' }) {
  const kind = knowledgeMediaKind(attachment);
  const { url, error, retry } = useKnowledgeEmbedUrl(attachment);
  const [mediaError, setMediaError] = useState(false);
  const automaticRetry = useRef({ attachmentId: attachment?.id, used: false });
  const classes = `knowledge-media knowledge-media-${variant} knowledge-media-${kind} ${className}`.trim();

  useEffect(() => {
    automaticRetry.current = { attachmentId: attachment?.id, used: false };
    setMediaError(false);
  }, [attachment?.id]);

  if (!attachment) return variant === 'cover' ? <div className={`${classes} empty`} aria-hidden="true">📄</div> : null;
  if (error || mediaError) {
    if (variant === 'cover') {
      return <div className={`${classes} error`} aria-hidden="true">📄</div>;
    }
    return (
      <div className={`${classes} error`}>
        <span>Не удалось показать «{attachment.name}»</span>
        <button type="button" onClick={() => {
          automaticRetry.current.used = false;
          setMediaError(false);
          retry();
        }}>Повторить</button>
      </div>
    );
  }
  if (!url) return <div className={`${classes} loading`} aria-label={`Загрузка ${attachment.name}`}><span className="spinner" /></div>;

  const refreshOnce = () => {
    if (automaticRetry.current.used) {
      setMediaError(true);
      return;
    }
    automaticRetry.current.used = true;
    retry();
  };

  if (kind === 'image') {
    return (
      <figure className={classes}>
        <img src={url} alt={variant === 'cover' ? '' : attachment.name} loading="lazy" onError={refreshOnce} />
        {variant !== 'cover' && <figcaption>{attachment.name}</figcaption>}
      </figure>
    );
  }
  if (kind === 'video') {
    return (
      <figure className={classes}>
        <video src={url} controls playsInline preload="metadata" onError={refreshOnce}>
          Ваш браузер не поддерживает встроенное видео.
        </video>
        <figcaption>{attachment.name}</figcaption>
      </figure>
    );
  }
  if (kind === 'pdf') {
    return (
      <figure className={classes}>
        <iframe src={`${url}#toolbar=1&navpanes=0`} title={attachment.name} loading="lazy" />
        <figcaption>{attachment.name}</figcaption>
      </figure>
    );
  }
  return (
    <a className={classes} href={url} target="_blank" rel="noopener noreferrer">
      <span className="knowledge-media-file-icon" aria-hidden="true">FILE</span>
      <span><strong>{attachment.name}</strong><small>Открыть файл</small></span>
    </a>
  );
}
